import { useNavigate } from "react-router-dom";
import { useCreator } from "../creator/CreatorContext";
import { AssetArt, PipMeter, Sprite } from "../design/primitives";
import { cardSprite } from "../design/sprites";
import { formatIcon } from "../design/formatIcon";
import { artCandidates } from "../library/coverUrl";
import { CARD_WASH, COVER_WASH, shade } from "../design/shade";
import type { PatronAsset } from "../data/types";
import css from "./assetCard.module.css";

/**
 * §5's card art system: no bespoke art for a new tab - only a hue.
 *
 * `compact` is the recently-added shape from §7's screenshots: art beside the
 * text rather than above it, no format chips, and the added date promoted to a
 * teal mono line - because on that strip *when* is the reason the card is
 * there. It is the same card, not a second component: one hover, one focus
 * ring, one click target, one place to fix them.
 */
export function AssetCard({
  asset,
  size,
  compact = false,
  onOpen,
}: {
  asset: PatronAsset;
  size?: number;
  compact?: boolean;
  /**
   * How to open this asset — R-9.
   *
   * The card used to navigate for itself. It cannot any more: opening a tab now
   * has to hand the detail screen the **ordered list the reader was looking
   * at**, and the card does not know that list. Only the screen holding the
   * filtered, sorted results does.
   *
   * Optional, so a card rendered outside the library (the gallery) still works.
   */
  onOpen?: (slug: string) => void;
}) {
  const { creator, gameById } = useCreator();
  const navigate = useNavigate();

  const game = gameById(asset.gameId);
  // A retired game still resolves - retirement is not deletion (D-12).
  const tint = game?.tint ?? "#8A7FB0";
  /*
   * Hoisted because the body needs to know too. On the compact card the art
   * used to supply the gap between itself and the text out of its own padding;
   * a cover fills the zone edge to edge, so that gap has to come from the body
   * instead — but only when there is a cover, or a sprite would get both.
   */
  const covers = artCandidates(asset, game, creator.publicAssetsBaseUrl);
  const typeDef = creator.assetTypes.find((t) => t.type === asset.assetType);
  const difficulty = Number(asset.fields.difficulty ?? 0);

  // The slot, not just its short name: the icon is chosen by `accept` (FS-D6).
  const formats = (typeDef?.slots ?? []).filter(
    (slot) => slot.shortName && asset.files[slot.id]
  );

  const open = () => (onOpen ? onOpen(asset.slug) : navigate(`/tabs/${asset.slug}`));

  const spriteSize = size ?? (compact ? 64 : 132);

  return (
    // §7: "Whole card clickable, tabindex=0, hover lift, focus ring."
    // §10: cards activate on Enter.
    <article
      className={compact ? `${css.card} ${css.compact}` : css.card}
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
    >
      {/*
        The tint stays behind the art either way — but not at the same strength.

        Behind a sprite it is a wash, pushed down to 0.16 so pixel art reads on
        top of it. Behind a cover it is the backdrop itself: the art is drawn
        whole, it leaves the zone showing either side, and what shows there
        should be the colour the creator picked for that game (COVER_WASH).

        Still one kind of card, one element, one rule — only the factor moves.
      */}
      <div
        className={css.art}
        style={{ background: shade(tint, covers.length ? COVER_WASH : CARD_WASH) }}
      >
        <AssetArt
          coverUrls={covers}
          spriteSrc={cardSprite(typeDef?.cardObject ?? "SHEET", tint)}
          size={spriteSize}
          /*
            Only here. The card's art zone is wider than its art, so a square
            cover leaves the tint wash showing either side; the blur fills that
            with the cover itself. The detail rail and the Studio table have no
            such gap - the art is the zone - so there is nothing to fill.
          */
          fillBackdrop
        />
      </div>
      <div className={covers.length ? `${css.body} ${css.bodyBesideCover}` : css.body}>
        {compact && <p className={css.added}>ADDED {formatAdded(asset.addedAt)}</p>}
        <h3 className={css.title}>{asset.title}</h3>
        <p className={css.game}>{game?.name ?? "Unknown"}</p>
        {compact ? (
          // The date is already the first line, so the meta row is pips alone.
          <div className={css.metaRow}>
            <PipMeter value={difficulty} size={10} />
          </div>
        ) : (
          <>
            <div className={css.metaRow}>
              <PipMeter value={difficulty} size={10} />
              <span className={css.date}>{formatAdded(asset.addedAt)}</span>
            </div>
            <div className={css.formats}>
              {formats.map((slot) => {
                const icon = formatIcon(slot.accept);
                return (
                  <span key={slot.id} className={css.formatChip}>
                    {icon && <Sprite src={icon} size={12} label={slot.shortName} />}
                    {slot.shortName!.toUpperCase()}
                  </span>
                );
              })}
            </div>
          </>
        )}
      </div>
    </article>
  );
}

export function formatAdded(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getUTCDate()} ${d
    .toLocaleString("en-GB", { month: "short", timeZone: "UTC" })
    .toUpperCase()} ${d.getUTCFullYear()}`;
}
